import { useEffect, useState } from "react";
import cx from "clsx";
import { createExport, getProducts, deleteProducts } from "../api";
import {
  Anchor,
  Button,
  Center,
  Checkbox,
  Container,
  Group,
  List,
  ListItem,
  Loader,
  Modal,
  Pagination,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import Timeago from "../../../Boilerplate/_Shared/Timeago";
import { Link } from "wouter";
import {
  IconAlertHexagon,
  IconAlertHexagonFilled,
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconFileTypeCsv,
  IconFileTypeXls,
  IconInfoCircle,
  IconSearch,
  IconSelector,
  IconTrash,
  IconWand,
} from "@tabler/icons-react";
import { useInterval } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import classes from "./ProductList.module.css";
import QualityBadge from "./QualityBadge";
import ProductStatusBadge from "./ProductStatusBadge";
import { notifications } from "@mantine/notifications";
import { ListingStatusTooltip } from "../Imports/StatusTooltip";

function Th({ children, reversed, sorted, onSort, ta }) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;
  return (
    <Table.Th ta={ta} className={classes.th}>
      <UnstyledButton onClick={onSort} className={classes.control}>
        <Group gap={4} justify="space-between" wrap="nowrap">
          <Text fw={500} fz="sm">
            {children}
          </Text>
          <Center className={classes.icon}>
            <Icon size={16} stroke={1.5} />
          </Center>
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

function filterData(data, search) {
  const query = search.toLowerCase().trim();
  if (!query) return data;
  return data.filter((item) => {
    return (
      (item.name ?? item.fieldValues.name)?.toLowerCase().includes(query) ||
      (item.productIdentifier ?? item.fieldValues.ean)?.includes(query)
    );
  });
}

function sortData(data, payload) {
  const { sortBy } = payload;

  if (!sortBy) {
    return filterData(data, payload.filter);
  }

  if (sortBy === "issues") {
    return filterData(
      [...data].sort((a, b) =>
        payload.reversed
          ? (a.validationFailures?.length ?? 0) -
            (b.validationFailures?.length ?? 0)
          : (b.validationFailures?.length ?? 0) -
            (a.validationFailures?.length ?? 0)
      ),
      payload.filter
    );
  }

  if (
    sortBy === "idCreated" ||
    sortBy === "status" ||
    sortBy === "flags" ||
    sortBy === "qualityScore" ||
    sortBy === "originalQualityScore"
  ) {
    return filterData(
      [...data].sort((a, b) => {
        if (payload.reversed) {
          return (b[sortBy] + "").localeCompare(a[sortBy] + "");
        }

        return (a[sortBy] + "").localeCompare(b[sortBy] + "");
      }),
      payload.filter
    );
  } else {
    return filterData(
      [...data].sort((a, b) => {
        if (payload.reversed) {
          return b.fieldValues[sortBy]?.localeCompare(a.fieldValues[sortBy]);
        }

        return a.fieldValues[sortBy]?.localeCompare(b.fieldValues[sortBy]);
      }),
      payload.filter
    );
  }
}

function ErrorTooltip({ validationFailures }) {
  const { t } = useTranslation("pim");
  return (
    <>
      {t("legacyProducts.issues")}
      <List
        size="md"
        spacing="xs"
        center
        icon={<IconAlertHexagonFilled size={18} color="red" />}
      >
        {validationFailures.map((error) => (
          <ListItem key={error.field + error.type}>
            <Text span fz="sm" fw={600}>
              '{error.field}':
            </Text>
            <Text span fz="sm">
              {error.message}
            </Text>
          </ListItem>
        ))}
      </List>
    </>
  );
}

export default function ProductsList({ params }) {
  const { t } = useTranslation("pim");
  const [products, setProducts] = useState(null);

  const [filter, setFilter] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [sortedData, setSortedData] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  const pageSize = 100;

  // Check if any products are in "processing" state
  const hasProcessingItems = products?.data?.some((product) =>
    product.flags.includes("processing")
  );

  // Create interval for auto-refresh
  const interval = useInterval(
    () => {
      getProducts(activePage, pageSize, params.mappingId).then((data) => {
        setProducts(data);
        setSortedData(
          sortData(data?.data, {
            sortBy,
            reversed: reverseSortDirection,
            filter,
          })
        );
      });
    },
    hasProcessingItems ? 5_000 : 60_000
  );

  // Initial load and when page/importId changes
  useEffect(() => {
    getProducts(activePage, pageSize, params.mappingId).then((data) => {
      setProducts(data);
      setSortedData(
        sortData(data?.data, { sortBy, reversed: reverseSortDirection, filter })
      );
    });
  }, [activePage, params?.importId]);

  // Adjust interval when processing status changes
  useEffect(() => {
    if (products) {
      interval.stop();
      interval.start();
    }

    return () => interval.stop();
  }, [hasProcessingItems, products]);

  // Start interval on initial mount
  useEffect(() => {
    interval.start();
    return () => interval.stop();
  }, []);

  const [selection, setSelection] = useState([]);
  const toggleRow = (id) =>
    setSelection((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  const toggleAll = () =>
    setSelection((current) =>
      current.length === products?.data?.length
        ? []
        : products?.data?.map((item) => item.id)
    );

  const setSorting = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(
      sortData(products?.data, { sortBy: field, reversed, filter })
    );
  };

  const handleSearchChange = (event) => {
    const { value } = event.currentTarget;
    setFilter(value);
    // console.log("SEARCH: ", value);
    setSortedData(
      sortData(products?.data, {
        sortBy,
        reversed: reverseSortDirection,
        filter: value,
      })
    );
  };

  const deleteSelected = () => {
    setIsExporting(true);
    setDeleteModalOpen(false);

    deleteProducts({ ids: selection, mappingId: params.mappingId })
      .then(() => {
        // Remove deleted products from the list
        setProducts((current) => {
          const newData = {
            ...current,
            data: current.data.filter(
              (product) => !selection.includes(product.id)
            ),
            totalCount: current.totalCount - selection.length,
          };
          return newData;
        });
        setSelection([]);
        notifications.show({
          title: t("legacyProducts.productsDeleted"),
          message: t("legacyProducts.productsDeletedMessage", {
            count: selection.length,
          }),
          autoClose: 5000,
        });
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  let rows = sortedData?.map((product) => {
    const selected = selection.includes(product.id);
    return (
      <Table.Tr
        className={cx({ [classes.rowSelected]: selected })}
        key={product.id}
      >
        <Table.Td>
          <Checkbox
            onClick={(e) => e.stopPropagation()}
            checked={selection.includes(product.id)}
            onChange={() => toggleRow(product.id)}
          />
        </Table.Td>
        <Table.Td>
          <Anchor component={Link} to={`/products/${product.id}`}>
            {product.productIdentifier ?? product.fieldValues.ean}
          </Anchor>
        </Table.Td>
        <Table.Td>
          {product.flags.split(",").map((flag) => (
            <ProductStatusBadge
              key={product.id + "status" + flag}
              status={flag.trim()}
            />
          ))}
        </Table.Td>
        <Table.Td ta="right">
          {product.validationFailures?.length > 0 ? (
            <Tooltip
              maw="40%"
              multiline
              label={
                <ErrorTooltip validationFailures={product.validationFailures} />
              }
              position="top"
              withArrow
            >
              <Group gap={6} align="center" justify="flex-end">
                <IconAlertHexagon size={20} color="red" />
                {product.validationFailures?.length}
              </Group>
            </Tooltip>
          ) : (
            <Text c="dimmed" ta="right">
              0
            </Text>
          )}
        </Table.Td>

        {/* TBD: We should have information about errors / validations here! */}
        <Table.Td>
          <Timeago date={product.idCreated} />
        </Table.Td>
        <Table.Td>
          <Anchor component={Link} to={`/products/${product.id}`}>
            {product.name ?? product.fieldValues.name ?? t("products.noName")}
          </Anchor>
        </Table.Td>
        <Table.Td>
          {(product.category ?? product.fieldValues.category)
            ?.split("/")
            .splice(-1)}
        </Table.Td>
        <Table.Td ta="right">
          <QualityBadge score={product.qualityScore} />
        </Table.Td>
        <Table.Td ta="right">
          <QualityBadge score={product.originalQualityScore} />
        </Table.Td>
      </Table.Tr>
    );
  });

  rows ??= []; // avoid NREs

  if (rows.length === 0 && !products) {
    rows = (
      <Table.Tr>
        <Table.Td colSpan={9}>
          <Stack align="center" justify="center" mih={80}>
            <Text ta="center" c="dimmed">
              {t("common.loading")}
            </Text>
            <Loader />
          </Stack>
        </Table.Td>
      </Table.Tr>
    );
  }

  if (rows.length === 0) {
    rows = (
      <Table.Tr>
        <Table.Td colSpan={9}>
          <Text ta="center" c="dimmed">
            {t("catalog.noProducts")}
          </Text>
        </Table.Td>
      </Table.Tr>
    );
  }

  return (
    <Container p={0} fluid>
      <Stack>
        {params.importId && (
          <Anchor onClick={() => window.history.back()} c="gray">
            {t("products.back")}
          </Anchor>
        )}
        <Title order={1}>
          {t("catalog.productCount", { count: products?.totalCount ?? 0 })}
        </Title>
      </Stack>

      <Group my="md" align="flex-end" justify="space-between">
        <Group>
          <Pagination
            hideWithOnePage={false}
            total={Math.max(1, Math.ceil(products?.totalCount / pageSize))}
            value={activePage}
            onChange={setActivePage}
          />

          <TextInput
            placeholder={t("legacyProducts.filter")}
            leftSection={<IconSearch size={16} stroke={1.5} />}
            value={filter}
            onChange={handleSearchChange}
            // value={filter}
            // onChange={(event) => setFilter(event.currentTarget.value)}
          />
        </Group>

        <Group>
          {" "}
          <Button
            disabled={selection.length === 0}
            loading={isExporting}
            size="sm"
            color="red"
            onClick={() => setDeleteModalOpen(true)}
            leftSection={<IconTrash size={18} stroke={1.5} />}
          >
            {t("products.delete", { count: selection.length })}
          </Button>
          <Button.Group>
            <Button
              variant="default"
              loading={isExporting}
              aria-label={t("legacyProducts.exportToXlsx")}
              leftSection={<IconDownload size={16} stroke={1.5} />}
              rightSection={<IconFileTypeXls size={16} stroke={1.5} />}
              onClick={async () => {
                setIsExporting(true);
                await createExport(params?.mappingId, {
                  ids: selection,
                  format: "xlsx",
                });
                setIsExporting(false);
              }}
            >
              .xlsx (
              {selection.length > 0 ? selection.length : products?.totalCount})
            </Button>
            <Button
              variant="default"
              loading={isExporting}
              aria-label={t("legacyProducts.exportToCsv")}
              onClick={async () => {
                setIsExporting(true);
                await createExport(params?.mappingId, {
                  ids: selection,
                  format: "csv",
                });
                setIsExporting(false);
              }}
              leftSection={<IconDownload size={16} stroke={1.5} />}
              rightSection={<IconFileTypeCsv size={16} stroke={1.5} />}
            >
              .csv (
              {selection.length > 0 ? selection.length : products?.totalCount})
            </Button>
          </Button.Group>
          <Button
            // disabled={selection.length === 0}
            loading={isExporting}
            size="sm"
            onClick={() => alert("not implemented")}
            leftSection={<IconWand size={18} stroke={1.5} />}
          >
            {t("legacyProducts.mapCount", {
              count:
                selection.length > 0 ? selection.length : products?.totalCount,
            })}
          </Button>
        </Group>
      </Group>

      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Checkbox
                onChange={toggleAll}
                checked={selection.length === products?.data?.length}
                indeterminate={
                  selection.length > 0 &&
                  selection.length !== products?.data?.length
                }
              />
            </Table.Th>
            <Th
              sorted={sortBy === "ean"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("ean")}
            >
              {t("legacyProducts.identifier")}
            </Th>
            <Th
              sorted={sortBy === "flags"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("flags")}
            >
              <Tooltip label={<ListingStatusTooltip />}>
                <span>
                  {t("legacyProducts.colStatus")}&nbsp;
                  <IconInfoCircle size={16} stroke={1.5} />
                </span>
              </Tooltip>
            </Th>

            <Th
              sorted={sortBy === "issues"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("issues")}
            >
              <Tooltip label={t("legacyProducts.issuesTooltip")}>
                <span>
                  {t("legacyProducts.issues")}&nbsp;
                  <IconInfoCircle size={16} stroke={1.5} />
                </span>
              </Tooltip>
            </Th>

            <Th
              sorted={sortBy === "idCreated"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("idCreated")}
            >
              {t("basics.created")}
            </Th>

            <Th
              sorted={sortBy === "name"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("name")}
            >
              {t("products.name")}
            </Th>

            <Th
              sorted={sortBy === "category"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("category")}
            >
              {t("grid.category")}
            </Th>

            <Th
              ta="right"
              sorted={sortBy === "qualityScore"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("qualityScore")}
            >
              {t("legacyProducts.currentScore")}
            </Th>

            <Th
              ta="right"
              sorted={sortBy === "originalQualityScore"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("originalQualityScore")}
            >
              {t("legacyProducts.originalScore")}
            </Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>

      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t("legacyProducts.confirmDeletion")}
      >
        <Text>
          {t("legacyProducts.deleteConfirm", { count: selection.length })}
        </Text>
        <Group mt="md" justify="flex-end">
          <Button onClick={() => setDeleteModalOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button color="red" onClick={deleteSelected}>
            {t("common.delete")}
          </Button>
        </Group>
      </Modal>
    </Container>
  );
}
